import { useMemo, useState } from "react";
import { TabItem } from "@hatch-digital-nl/bravilor-storybook";
import { useStringTranslation } from "../useStringTranslation";
import { ProductGroupedDownloadItemInterface } from "../../interfaces/apiV2/product/ProductDownloadsInterface";
import {
    buildProductDownloadEntries,
    hasProductDownloadContent,
    ProductDownloadEntry,
} from "../../utils/productDetail/buildProductDownloadEntries";
import {
    PRODUCT_DOWNLOAD_TAB,
    ProductDownloadTabId,
} from "../../utils/productDetail/productDownloadsConstants";

interface UseProductDownloadsParams {
    marketingDownloads: Record<string, ProductGroupedDownloadItemInterface[]>;
    technicalDownloads: Record<string, ProductGroupedDownloadItemInterface[]>;
    marketingDownloadsCount: number;
    technicalDownloadsCount: number;
}

interface UseProductDownloadsReturn {
    hasContent: boolean;
    showTabs: boolean;
    tabs: TabItem[];
    cardTitle: string;
    downloadsLabel: string;
    activeTabId: ProductDownloadTabId;
    setActiveTabId: React.Dispatch<React.SetStateAction<ProductDownloadTabId>>;
    downloadEntries: ProductDownloadEntry[];
}

export const useProductDownloads = ({
    marketingDownloads,
    technicalDownloads,
    marketingDownloadsCount,
    technicalDownloadsCount,
}: UseProductDownloadsParams): UseProductDownloadsReturn => {
    const { t } = useStringTranslation();

    const hasTechnical =
        technicalDownloadsCount > 0 ||
        hasProductDownloadContent(technicalDownloads);
    const hasMarketing =
        marketingDownloadsCount > 0 ||
        hasProductDownloadContent(marketingDownloads);

    const showTabs = hasTechnical && hasMarketing;

    const [activeTabId, setActiveTabId] = useState<ProductDownloadTabId>(
        hasTechnical
            ? PRODUCT_DOWNLOAD_TAB.TECHNICAL
            : PRODUCT_DOWNLOAD_TAB.MARKETING,
    );

    const technicalLabel = t("technical-downloads");
    const marketingLabel = t("marketing-downloads");

    const tabs: TabItem[] = useMemo(
        () => [
            {
                id: PRODUCT_DOWNLOAD_TAB.TECHNICAL,
                label: technicalLabel,
                count: technicalDownloadsCount,
            },
            {
                id: PRODUCT_DOWNLOAD_TAB.MARKETING,
                label: marketingLabel,
                count: marketingDownloadsCount,
            },
        ],
        [
            technicalLabel,
            marketingLabel,
            technicalDownloadsCount,
            marketingDownloadsCount,
        ],
    );

    const activeTab = showTabs
        ? activeTabId
        : hasTechnical
          ? PRODUCT_DOWNLOAD_TAB.TECHNICAL
          : PRODUCT_DOWNLOAD_TAB.MARKETING;

    const isMarketing = activeTab === PRODUCT_DOWNLOAD_TAB.MARKETING;
    const currentDownloads = isMarketing
        ? marketingDownloads
        : technicalDownloads;

    const cardTitle = isMarketing ? marketingLabel : technicalLabel;
    const downloadsLabel = hasMarketing ? marketingLabel : technicalLabel;

    const downloadEntries = useMemo(
        () => buildProductDownloadEntries(currentDownloads),
        [currentDownloads],
    );

    const hasContent =
        (hasTechnical || hasMarketing) && downloadEntries.length > 0;

    return {
        hasContent,
        showTabs,
        tabs,
        cardTitle,
        downloadsLabel,
        activeTabId,
        setActiveTabId,
        downloadEntries,
    };
};
